import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { UsageDashboardData, UsagePeriod } from '@respectly/shared-types';

interface UsageError {
  code: string;
  message: string;
}

interface UsageState {
  data: UsageDashboardData | null;
  isLoading: boolean;
  error: UsageError | null;
  period: UsagePeriod;
  lastUpdatedAt: Date | null;

  // Actions
  loadDashboard: () => Promise<void>;
  setPeriod: (period: UsagePeriod) => void;
  clearError: () => void;
}

export const useUsageStore = create<UsageState>((set, get) => ({
  data: null,
  isLoading: false,
  error: null,
  period: '30d',
  lastUpdatedAt: null,

  loadDashboard: async () => {
    const { period } = get();
    set({ isLoading: true, error: null });
    try {
      const api = getApiClient();
      const data = await api.get<UsageDashboardData>(
        `${API_ENDPOINTS.SUPER_ADMIN.USAGE.DASHBOARD}?period=${period}`
      );
      set({ data, isLoading: false, lastUpdatedAt: new Date() });
    } catch (error) {
      set({
        isLoading: false,
        error: {
          code: 'LOAD_USAGE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load usage analytics',
        },
      });
    }
  },

  setPeriod: (period: UsagePeriod) => {
    set({ period });
    get().loadDashboard();
  },

  clearError: () => set({ error: null }),
}));
